import { getAccessToken } from '@/providers/loginWithEmail/dataProvider';
import { ContractSignerParty, ProjectContractSigning } from '@/types';

/** Shown in the re-save confirm dialog (EstimatorPage, QuoteAside). Keep the wording exact. */
export const CONTRACT_SIGNING_VOID_MESSAGE =
    'The contract has already been sent for signing. If you continue, the signing will be voided and sent again.';

const DEFAULT_DOCUSEAL_HOST = 'https://docuseal.com';

const apiBase = () => import.meta.env.VITE_APP_API_BACKEND;

type ErrorDetail = { root?: string; code?: string };

type ErrorBody = {
    detail?: ErrorDetail | string;
    errors?: unknown;
};

export class ContractSigningApiError extends Error {
    readonly status: number;
    readonly code?: string;

    constructor(message: string, status: number, code?: string) {
        super(message);
        this.name = 'ContractSigningApiError';
        this.status = status;
        this.code = code;
    }
}

/** True when a save/regenerate response asks the user to confirm voiding an active signing. */
export const isContractSigningActiveError = (status: number, body: unknown): boolean => {
    if (status !== 409) return false;
    const detail = (body as ErrorBody | null | undefined)?.detail;
    return (
        typeof detail === 'object' &&
        detail !== null &&
        detail.code === 'contract_signing_active'
    );
};

/** Embed URL for a DocuSeal submitter slug. The host is configurable via VITE_DOCUSEAL_HOST. */
export const docusealSigningUrl = (slug: string): string => {
    const host = (import.meta.env.VITE_DOCUSEAL_HOST || DEFAULT_DOCUSEAL_HOST).replace(/\/$/, '');
    return `${host}/s/${slug}`;
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = getAccessToken();
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(`${apiBase()}${path}`, { ...init, headers });
    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as ErrorBody;
        const detail = body.detail;
        const message =
            typeof detail === 'string'
                ? detail
                : detail?.root || 'Contract signing request failed';
        const code = typeof detail === 'object' && detail !== null ? detail.code : undefined;
        throw new ContractSigningApiError(message, response.status, code);
    }
    return (await response.json()) as T;
}

export const fetchProjectContractSigning = (
    projectId: number | string,
): Promise<ProjectContractSigning> =>
    request<ProjectContractSigning>(`/projects/${projectId}/contract-signing`);

export const startContractSigning = (
    projectId: number | string,
    signerParty?: ContractSignerParty,
): Promise<ProjectContractSigning> =>
    request<ProjectContractSigning>(`/projects/${projectId}/contract-signing`, {
        method: 'POST',
        ...(signerParty
            ? {
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ signer_party: signerParty }),
              }
            : {}),
    });

export const cancelContractSigning = (
    projectId: number | string,
): Promise<ProjectContractSigning> =>
    request<ProjectContractSigning>(`/projects/${projectId}/contract-signing`, {
        method: 'DELETE',
    });

/** Fetch the archived signed-contract PDF and return an object URL for preview. */
export const fetchSignedContractPdfUrl = async (
    projectId: number | string,
): Promise<string> => {
    const token = getAccessToken();
    const headers = new Headers();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(
        `${apiBase()}/projects/${projectId}/contract-signing/signed-pdf`,
        { headers },
    );
    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as ErrorBody;
        const detail = body.detail;
        const message =
            typeof detail === 'string'
                ? detail
                : detail?.root || 'Could not load the signed contract';
        const code = typeof detail === 'object' && detail !== null ? detail.code : undefined;
        throw new ContractSigningApiError(message, response.status, code);
    }
    return URL.createObjectURL(await response.blob());
};
